import Vue from 'vue';
import Vuex from 'vuex';
import FirebaseService from '../services/FirebaseService';
import EventService from '../services/EventService';
import axios from 'axios';
import type {User} from 'firebase/auth';
import type {GroupRes} from '@/services/types';

Vue.use(Vuex);

const scopeType = {
  'apis.wacwac-audit-dev.com/truck': 'truck',
  'apis.wacwac-audit-dev.com/forklift': 'forklift',
} as const;

export type ScopeType = typeof scopeType;
export type ScopeKey = keyof ScopeType;
export type Scope = (typeof scopeType)[ScopeKey];
export const AllScopeKeys = Object.keys(scopeType);

// TODO (ohtsuchi) 2025年以降 コードを削除
export const ConfSortByValue = {
  Default: '0',
  Changed: '1',
} as const;

// TODO (ohtsuchi) 2025年以降 コードを削除
export type ConfSortByType =
  (typeof ConfSortByValue)[keyof typeof ConfSortByValue];

export function GetScopeFromKey(key: ScopeKey): Scope | never {
  switch (key) {
    case 'apis.wacwac-audit-dev.com/truck':
    case 'apis.wacwac-audit-dev.com/forklift':
      return scopeType[key];
    default: {
      const _keyCheck: never = key;
      return _keyCheck;
    }
  }
}

export default new Vuex.Store({
  state: {
    newsCount: 0,
    drawer: true,
    isGroup: false,
    // 営業所グループの名称(統括アカウントでログイン中) or 営業所の名称(営業所アカウントでログイン中)
    accountName: undefined,
    // 営業所のID (変更タイミング: 統括アカウントでログイン中 && 営業所が決定する時)
    current: '',
    // 営業所の名称 (変更タイミング: 統括アカウントでログイン中 && 営業所が決定する時)
    currentName: '',
    scope: [] as Array<string>,
    currentScope: '',
    // current と 同じ 営業所グループ && 同じ scope && 同じ fiscalStartMonthDate の 営業所一覧
    sameGroupBranchOfficeList: [] as Array<GroupRes>,
    // 12項目,ヒヤリハットの区別無し[開始年] (変更タイミング: 営業所(スコープ)が決定する時)
    noSubGroupStartYear: 0,
    user: undefined as User | undefined,
    sortBy: ConfSortByValue.Default,
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    CLEAR_USER(state) {
      state.user = undefined;
    },
    SET_SCOPE(state, scope) {
      if (Array.isArray(scope)) {
        state.scope = scope;
      }
    },
    SET_CURRENTSCOPE(state, scope) {
      state.currentScope = scope;
    },
    CLEAR_SCOPE(state) {
      state.scope = [];
      state.currentScope = '';
      state.noSubGroupStartYear = 0;
    },
    SET_NO_SUB_GROUP_START_YEAR(state, noSubGroupStartYear) {
      state.noSubGroupStartYear = noSubGroupStartYear;
    },
    toggleDrawer(state) {
      state.drawer = !state.drawer;
    },
    SET_IS_GROUP(state, isGroup) {
      state.isGroup = isGroup;
    },
    SET_USER_INFO(state, name) {
      state.accountName = name;
    },
    SET_CURRENT(state, companyID) {
      state.current = companyID;
    },
    CLEAR_CURRENT(state) {
      state.current = '';
    },
    SET_CURRENT_NAME(state, companyName) {
      state.currentName = companyName;
    },
    CLEAR_CURRENT_NAME(state) {
      state.currentName = '';
    },
    SET_SAME_GROUP_BRANCH_OFFICE_LIST(
      state,
      sameGroupBranchOfficeList: Array<GroupRes>
    ) {
      state.sameGroupBranchOfficeList = sameGroupBranchOfficeList;
    },
    CLEAR_SAME_GROUP_BRANCH_OFFICE_LIST(state) {
      state.sameGroupBranchOfficeList = [];
    },
    SET_NEWS_COUNT(state, count) {
      state.newsCount = count;
    },
    // TODO (ohtsuchi) 2025年以降 コードを削除
    SET_SORT_BY(state, confSortBy) {
      state.sortBy = confSortBy;
    },
    // TODO (ohtsuchi) 2025年以降 コードを削除
    CLEAR_SORT_BY(state) {
      state.sortBy = ConfSortByValue.Default;
    },
  },
  actions: {
    scope: async ({commit, state}) => {
      const token = await state.user?.getIdToken();
      const headers: {[k: string]: string} = {
        Authorization: `Bearer ${token}`,
      };
      const companyID = state.current;
      if (companyID !== '') {
        headers['x-company-id'] = companyID;
      }
      const {data} = await axios.get(`${EventService.ProtoURL()}/scope/`, {
        headers,
      });
      commit('SET_SCOPE', data.scope);

      if (data.scope.length > 0) {
        commit('SET_CURRENTSCOPE', data.scope[0]);
      }
      if (data.noSubGroupStartYear) {
        commit('SET_NO_SUB_GROUP_START_YEAR', data.noSubGroupStartYear);
      } else {
        commit('SET_NO_SUB_GROUP_START_YEAR', 0);
      }
    },
    news: async ({commit, state}) => {
      const token = await state.user?.getIdToken();
      const headers: {[k: string]: string} = {
        Authorization: `Bearer ${token}`,
      };
      const companyID = state.current;
      if (companyID !== '') {
        headers['x-company-id'] = companyID;
      }

      const {data} = await axios.get<{IsNew: boolean}[]>(
        `${EventService.ProtoURL()}/news/`,
        {
          withCredentials: true,

          headers: headers,
        }
      );
      if (data) {
        const filtered = data.filter(item => {
          return item.IsNew === true;
        });
        commit('SET_NEWS_COUNT', filtered.length);
      }
    },
    login: async ({commit}, credentials) => {
      const user = await FirebaseService.auth(
        credentials.email,
        credentials.password
      );
      commit('SET_USER', user);
      commit('SET_USER_INFO', user?.displayName);
    },
    logout: async ({commit}) => {
      console.log('logout');
      await FirebaseService.logout();
      commit('CLEAR_USER');
      commit('CLEAR_SCOPE');
      // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここから
      commit('CLEAR_SORT_BY');
      // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここまで
    },
  },
  modules: {},
  getters: {
    isLogin(state) {
      return !!state.user;
    },
    getCurrent(state) {
      if (AllScopeKeys.includes(state.currentScope)) {
        return scopeType[state.currentScope as ScopeKey];
      } else {
        return 'Unknown';
      }
    },
  },
});
